import React from 'react';
import { View, StyleSheet, Platform } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { ResioLogo } from '@/assets/ResioLogo';
import { useNavigation } from '@react-navigation/native';
import { useAppDispatch, useAppSelector } from '@/store';
import { logout } from '@/store/slices/authSlice';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { WebConfirmModal, ModalThemeColors } from '@/components/common/modals';
import { strings } from '@/constants/residents';
import { useUnreadNotificationsCount } from '@/hooks/common/useUnreadNotificationsCount';
import DesktopNotificationsPopover from '../notification/DesktopNotificationsPopover';
import { useThemeColors } from '@/hooks/residents/useThemeColors';

function useModalThemeColors(): ModalThemeColors {
  const { colors, semantic } = useThemeColors();
  return {
    overlay: 'rgba(0,0,0,0.5)',
    modalBg: colors.bg.modal,
    modalBorder: colors.card.border,
    titleText: colors.text.primary,
    messageText: colors.text.secondary,
    cancelBg: colors.bg.base,
    cancelBorder: colors.card.border,
    cancelText: colors.text.primary,
    confirmBg: semantic.interactive.danger,
    confirmText: semantic.text.inverse,
  };
}

type DesktopHeaderProps = {
  title?: string;
  rightContent?: React.ReactNode;
  onNavigateToBulletins?: () => void;
  onNavigateToNotificationSettings?: () => void;
  onNavigateToChat?: () => void;
};

export const DesktopHeader: React.FC<DesktopHeaderProps> = ({ title, rightContent, onNavigateToBulletins, onNavigateToNotificationSettings, onNavigateToChat }) => {
  const { colors } = useThemeColors();
  const modalColors = useModalThemeColors();
  const navigation = useNavigation<any>();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((s) => s.auth);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const { total: unreadCount } = useUnreadNotificationsCount();
  const [showNotifications, setShowNotifications] = React.useState(false);
  const onNotifications = () => setShowNotifications((v) => !v);
  const onSignOut = () => {
    if (Platform.OS === 'web') {
      setShowConfirm(true);
      return;
    }
    dispatch(logout());
  };

  return (
    <>
      <WebConfirmModal
        visible={showConfirm}
        title={strings.logout.desktop.title}
        message={strings.logout.desktop.message}
        confirmText={strings.logout.desktop.confirmText}
        cancelText={strings.logout.desktop.cancelText}
        onConfirm={() => {
          setShowConfirm(false);
          dispatch(logout());
        }}
        onCancel={() => setShowConfirm(false)}
        themeColors={modalColors}
      />
      <View style={[styles.container, { backgroundColor: colors.bg.modal, borderBottomColor: colors.separator }]}>
      <View style={styles.leftGroup}>
        <ResioLogo width={64} height={64} />
        <Text numberOfLines={1} style={[styles.title, { color: colors.text.secondary }]}>
          RESIO
        </Text>
      </View>
      <View style={styles.right}>
        {rightContent}
        <View>
          <Hoverable hover="glass"
            accessibilityRole="button"
            accessibilityLabel="Notifications"
            onPress={onNotifications}
            style={[styles.iconButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
          >
            <RESIDENTS_SVG_COMPONENTS.BELL width={20} height={20} color={colors.text.muted} />
            {unreadCount > 0 && (
              <View style={[styles.unreadDot, { backgroundColor: colors.accent.red, borderColor: colors.bg.modal }]} accessibilityLabel="Unread notifications" />
            )}
          </Hoverable>
          {showNotifications && (
            <DesktopNotificationsPopover
              onClose={() => setShowNotifications(false)}
              navigateToAll={() => {
                setShowNotifications(false);
                if (onNavigateToBulletins) onNavigateToBulletins();
                // eslint-disable-next-line no-empty -- intentional: navigation fallback handled by else branch
                else try { navigation.navigate('Bulletins'); } catch {}
              }}
              navigateToSettings={() => {
                setShowNotifications(false);
                if (onNavigateToNotificationSettings) onNavigateToNotificationSettings();
                // eslint-disable-next-line no-empty -- intentional: navigation fallback handled by else branch
                else try { navigation.navigate('Account', { section: 'notificationSettings' }); } catch {}
              }}
              navigateToChat={() => {
                setShowNotifications(false);
                if (onNavigateToChat) onNavigateToChat();
                // eslint-disable-next-line no-empty -- intentional: navigation fallback handled by else branch
                else try { navigation.navigate('Messages'); } catch {}
              }}
            />
          )}
        </View>
        <Hoverable hover="glass" accessibilityRole="button" accessibilityLabel="Sign out" onPress={onSignOut} style={[styles.signOutButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}>
          <Text style={[styles.signOutText, { color: colors.text.secondary }]}>Sign out</Text>
        </Hoverable>
      </View>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 24,
    paddingTop: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    alignItems: 'center',
    flexDirection: 'row',
    zIndex: 1000,
    elevation: 16,
  },
  leftGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  title: {
    marginLeft: 12,
    fontSize: 48,
    fontWeight: '700',
    letterSpacing: 1,
  },
  right: {
    marginLeft: 'auto',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  iconButton: {
    height: 40,
    width: 40,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  unreadDot: {
    position: 'absolute',
    top: 4,
    right: 6,
    width: 10,
    height: 10,
    borderRadius: 5,
    borderWidth: 2,
  },
  signOutButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
  },
  signOutText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
